import React, { useRef } from "react";
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Stack,
  useTheme,
  alpha,
  Button,
  Chip,
} from "@mui/material";
import {
  Map as MapIcon,
  Analytics as AnalyticsIcon,
  Storage as StorageIcon,
  Build as BuildIcon,
  Satellite as SatelliteIcon,
  Timeline as TimelineIcon,
  ArrowForward as ArrowForwardIcon,
} from "@mui/icons-material";
import Footer from "../Components/Utils/Footer";
import Header from "../Components/Utils/Header";
import Hero from "../Components/Landing/Hero";
import FAQ from "../Components/Utils/FAQ";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";

const GIS_SERVICES = [
  {
    icon: MapIcon,
    title: "Mapping & Visualization",
    description:
      "Create interactive maps and compelling visualizations that bring your spatial data to life. Perfect for urban planning and asset management.",
    color: "primary",
    path: "/contact",
    features: ["Interactive Maps", "Data Visualization", "Custom Styling"],
  },
  {
    icon: AnalyticsIcon,
    title: "Spatial Analysis",
    description:
      "Advanced spatial analysis to uncover patterns, trends, and insights from your geographic data. Make informed decisions with confidence.",
    color: "secondary",
    path: "/contact",
    features: ["Pattern Analysis", "Trend Detection", "Statistical Analysis"],
  },
  {
    icon: StorageIcon,
    title: "Database Management",
    description:
      "Robust spatial database design and management using PostgreSQL/PostGIS. Efficient storage and retrieval of your geographic data.",
    color: "info",
    path: "/contact",
    features: [
      "PostgreSQL/PostGIS",
      "Data Migration",
      "Performance Optimization",
    ],
  },
  {
    icon: BuildIcon,
    title: "Custom Solutions",
    description:
      "Tailored GIS applications and tools designed to meet your specific needs. Web-based and desktop solutions available.",
    color: "success",
    path: "/contact",
    features: ["Custom Applications", "API Integration", "Workflow Automation"],
  },
  {
    icon: SatelliteIcon,
    title: "Remote Sensing",
    description:
      "Satellite imagery analysis and processing. Extract valuable insights from aerial and satellite data for environmental monitoring.",
    color: "warning",
    path: "/contact",
    features: [
      "Image Processing",
      "Change Detection",
      "Environmental Monitoring",
    ],
  },
  {
    icon: TimelineIcon,
    title: "3D Modeling",
    description:
      "Create detailed 3D terrain models and visualizations. Perfect for urban planning, architecture, and environmental studies.",
    color: "error",
    path: "/contact",
    features: ["Terrain Modeling", "3D Visualization", "Virtual Reality"],
  },
];

const GIS_PORTFOLIO = [
  {
    title: "Urban Planning Portal",
    description:
      "Interactive web-based GIS application for city planning and zoning management",
    image: require("../assets/images/gis1.png"),
    tags: ["Web GIS", "Urban Planning", "Zoning"],
    path: "/contact",
  },
  {
    title: "Environmental Monitoring",
    description:
      "Satellite imagery analysis system for tracking environmental changes",
    image: require("../assets/images/gis2.png"),
    tags: ["Remote Sensing", "Environmental", "Analysis"],
    path: "/contact",
  },
  {
    title: "Asset Management System",
    description:
      "Comprehensive GIS solution for infrastructure asset management",
    image: require("../assets/images/gis3.png"),
    tags: ["Asset Management", "Infrastructure", "Database"],
    path: "/contact",
  },
];

const GIS_FAQS = [
  {
    question: "What is GIS and how can it benefit my organization?",
    answer:
      "Geographic Information Systems (GIS) is a framework for gathering, managing, and analyzing spatial data. It helps organizations make better decisions by understanding geographic patterns and relationships. Benefits include improved efficiency, better decision-making, and enhanced communication through maps and spatial analysis.",
  },
  {
    question: "How long does a typical GIS project take?",
    answer:
      "Project timelines vary depending on complexity and scope. Simple mapping projects might take 1-2 weeks, while comprehensive GIS systems could take 2-3 months. We'll provide a detailed timeline during our initial consultation.",
  },
  {
    question: "Do you provide training for GIS systems?",
    answer:
      "Yes, we offer comprehensive training programs tailored to your team's needs. This includes basic GIS concepts, software usage, and advanced analysis techniques. We ensure your team can effectively use and maintain the GIS solutions we develop.",
  },
  {
    question: "What software and technologies do you use?",
    answer:
      "We work with industry-leading GIS software including ESRI ArcGIS, QGIS, PostgreSQL/PostGIS, and various web mapping technologies. We choose the most appropriate tools based on your specific needs and budget.",
  },
  {
    question: "Can you integrate GIS with our existing systems?",
    answer:
      "Yes, we specialize in integrating GIS solutions with existing business systems including ERP, CRM, and asset management systems. We ensure seamless data flow and functionality across your organization.",
  },
];

const GIS = React.forwardRef((props, ref) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const gisRef = useRef(null);

  const handleServiceClick = (path) => {
    navigate(path);
  };

  const handlePortfolioClick = (path) => {
    navigate(path);
  };

  return (
    <Box sx={{ minHeight: "100vh" }}>
      <Header active="GIS" />
      <Hero index={1} />

      <Box ref={ref || gisRef} sx={{ py: 8, bgcolor: "background.default" }}>
        <Container>
          <Stack spacing={8}>
            {/* Services Section */}
            <Stack spacing={3} sx={{ textAlign: "center" }}>
              <Typography variant="h3" sx={{ fontWeight: 700 }}>
                GIS Services
              </Typography>
              <Typography
                variant="body1"
                sx={{ color: "text.secondary", maxWidth: 800, mx: "auto" }}
              >
                Leverage the power of geographic information systems to
                transform your spatial data into actionable insights.
              </Typography>
            </Stack>

            <Grid container spacing={3}>
              {GIS_SERVICES.map((service, index) => (
                <Grid item xs={12} sm={6} md={4} key={service.title}>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <Card
                      elevation={0}
                      onClick={() => handleServiceClick(service.path)}
                      sx={{
                        height: "100%",
                        transition: "all 0.3s ease-in-out",
                        cursor: "pointer",
                        border: `1px solid ${alpha(
                          theme.palette[service.color].main,
                          0.1
                        )}`,
                        "&:hover": {
                          transform: "translateY(-8px)",
                          boxShadow: theme.shadows[8],
                          borderColor: "transparent",
                          bgcolor: alpha(
                            theme.palette[service.color].main,
                            0.02
                          ),
                        },
                      }}
                    >
                      <CardContent sx={{ p: 4 }}>
                        <Stack spacing={3}>
                          <Box
                            sx={{
                              width: 56,
                              height: 56,
                              borderRadius: 2,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              bgcolor: alpha(
                                theme.palette[service.color].main,
                                0.1
                              ),
                              transition: "transform 0.3s ease-in-out",
                              "& svg": {
                                transition: "transform 0.3s ease-in-out",
                              },
                              ".MuiCard-root:hover &": {
                                transform: "scale(1.1)",
                                "& svg": {
                                  transform: "scale(1.2)",
                                },
                              },
                            }}
                          >
                            <service.icon
                              sx={{
                                fontSize: 32,
                                color: `${service.color}.main`,
                              }}
                            />
                          </Box>

                          <Box>
                            <Typography
                              variant="h5"
                              gutterBottom
                              sx={{ fontWeight: 600 }}
                            >
                              {service.title}
                            </Typography>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ mb: 2 }}
                            >
                              {service.description}
                            </Typography>
                            <Stack direction="row" flexWrap="wrap" gap={1}>
                              {service.features.map((feature) => (
                                <Chip
                                  key={feature}
                                  label={feature}
                                  size="small"
                                  sx={{
                                    bgcolor: alpha(
                                      theme.palette[service.color].main,
                                      0.1
                                    ),
                                    color: `${service.color}.main`,
                                  }}
                                />
                              ))}
                            </Stack>
                          </Box>

                          <Button
                            variant="outlined"
                            color={service.color}
                            endIcon={<service.icon />}
                            sx={{
                              mt: "auto",
                              alignSelf: "flex-start",
                              "&:hover": {
                                transform: "translateX(4px)",
                              },
                            }}
                          >
                            Learn More
                          </Button>
                        </Stack>
                      </CardContent>
                    </Card>
                  </motion.div>
                </Grid>
              ))}
            </Grid>

            {/* Portfolio Section */}
            <Box sx={{ py: { xs: 4, md: 6 }, bgcolor: "background.paper" }}>
              <Container>
                <Stack spacing={6}>
                  <Stack spacing={2} sx={{ textAlign: "center" }}>
                    <Typography variant="h3" sx={{ fontWeight: 700 }}>
                      Featured Projects
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{ color: "text.secondary" }}
                    >
                      Explore our successful GIS implementations across various
                      industries
                    </Typography>
                  </Stack>

                  <Grid container spacing={3}>
                    {GIS_PORTFOLIO.map((project, index) => (
                      <Grid item xs={12} md={4} key={project.title}>
                        <motion.div
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.1 }}
                        >
                          <Card
                            elevation={0}
                            onClick={() => handlePortfolioClick(project.path)}
                            sx={{
                              height: "100%",
                              cursor: "pointer",
                              transition: "all 0.3s ease-in-out",
                              border: `1px solid ${alpha(
                                theme.palette.primary.main,
                                0.1
                              )}`,
                              "&:hover": {
                                transform: "translateY(-8px)",
                                boxShadow: theme.shadows[8],
                                borderColor: "transparent",
                                "& .MuiCardMedia-root": {
                                  transform: "scale(1.1)",
                                },
                              },
                            }}
                          >
                            <Box
                              sx={{ position: "relative", overflow: "hidden" }}
                            >
                              <Box
                                component="img"
                                src={project.image}
                                alt={project.title}
                                sx={{
                                  width: "100%",
                                  height: 240,
                                  objectFit: "cover",
                                  transition: "transform 0.3s ease-in-out",
                                }}
                              />
                              <Box
                                sx={{
                                  position: "absolute",
                                  bottom: 0,
                                  left: 0,
                                  right: 0,
                                  bgcolor: "rgba(0, 0, 0, 0.6)",
                                  color: "common.white",
                                  p: 2,
                                  transform: "translateY(100%)",
                                  transition: "transform 0.3s ease-in-out",
                                  ".MuiCard-root:hover &": {
                                    transform: "translateY(0)",
                                  },
                                }}
                              >
                                <Typography variant="body2">
                                  Click to Learn More
                                </Typography>
                              </Box>
                            </Box>
                            <CardContent sx={{ p: 3 }}>
                              <Stack spacing={2}>
                                <Typography
                                  variant="h5"
                                  sx={{ fontWeight: 600 }}
                                >
                                  {project.title}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  {project.description}
                                </Typography>
                                <Stack direction="row" flexWrap="wrap" gap={1}>
                                  {project.tags.map((tag) => (
                                    <Chip
                                      key={tag}
                                      label={tag}
                                      size="small"
                                      sx={{
                                        bgcolor: alpha(
                                          theme.palette.primary.main,
                                          0.1
                                        ),
                                        color: "primary.main",
                                      }}
                                    />
                                  ))}
                                </Stack>
                                <Button
                                  variant="outlined"
                                  color="primary"
                                  endIcon={<ArrowForwardIcon />}
                                  sx={{
                                    mt: 1,
                                    "&:hover": {
                                      transform: "translateX(4px)",
                                    },
                                  }}
                                >
                                  View Details
                                </Button>
                              </Stack>
                            </CardContent>
                          </Card>
                        </motion.div>
                      </Grid>
                    ))}
                  </Grid>
                </Stack>
              </Container>
            </Box>
          </Stack>
        </Container>
      </Box>

      {/* FAQ Section */}
      <FAQ
        title="GIS Services FAQs"
        subtitle="Common questions about our GIS services and solutions"
        faqs={GIS_FAQS}
      />

      <Footer />
    </Box>
  );
});

export default GIS;
